/**
 * The event registry: every event type this system knows about, with a Zod schema for its
 * payload. Client and server import the same registry, so validation cannot drift between
 * what a phone is willing to record and what the server is willing to accept.
 *
 * Phase 0 covers the spine only — users, devices, configuration. Domain events for the
 * catalog, POS, inventory, delivery, books and payroll are added here as each phase lands.
 * Adding an event type is a one-line change in this object plus a reducer in @abastece/core.
 *
 * Event types are past-tense Spanish, because the domain is Spanish and the people who will
 * read these records in a bitácora are reading them in Spanish.
 */

import { z } from 'zod';
import { ROLES } from '../roles.js';
import { ubicacionSchema } from '../ubicaciones.js';
import { fotoHashSchema } from '../fotos.js';
import { CODIGO_MAX } from '../codigos.js';
import { categoriaGastoSchema, formaPagoGastoSchema, periodoSchema } from '../gastosFijos.js';
import { FRECUENCIAS_PAGO } from '../planilla.js';
import {
  CUENTAS_BANCO,
  cuentaBancoCordobasSchema,
  cuentaBancoDolaresSchema,
  cuentaBancoSchema,
  esCuentaEnDolares,
} from '../bancos.js';

/**
 * The bank account money went to or came from, by transfer (`bancos.ts`, 2026-10-10): absent, the
 * first córdoba account, «Bancos», which is where every transfer went before there were others.
 */
const cuentaDeTransferencia = cuentaBancoCordobasSchema.optional();

/** A bank account is named only for what went by transfer. */
function cuentaSoloConTransferencia(
  v: { cuenta?: string | undefined },
  forma: string | undefined,
  ctx: z.RefinementCtx,
): void {
  if (v.cuenta !== undefined && forma !== 'transferencia') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cuenta'], message: 'Solo una transferencia va a una cuenta de banco' });
  }
}

/** The owner's money in or out of a dollar account is said in its dollars; anywhere else, in córdobas. */
function dolaresDelDueno(v: { cuenta?: string | undefined; dolares?: number | undefined }, forma: string, ctx: z.RefinementCtx): void {
  cuentaSoloConTransferencia(v, forma, ctx);
  const enDolares = v.cuenta !== undefined && esCuentaEnDolares(v.cuenta);
  if (enDolares && v.dolares === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dolares'], message: 'Falta cuántos dólares' });
  }
  if (!enDolares && v.dolares !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dolares'], message: 'Solo una cuenta en dólares lleva dólares' });
  }
}

/** A dollar account pays only what is in dollars: what it moves is its dollars. */
function dolaresSoloEnDolares(v: { cuenta?: string | undefined; moneda?: string }, ctx: z.RefinementCtx): void {
  if (v.cuenta !== undefined && esCuentaEnDolares(v.cuenta) && v.moneda !== 'USD') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cuenta'], message: 'Una cuenta en dólares paga solo lo que es en dólares' });
  }
}

/**
 * What somebody is paid, said whole (`planilla.ts`, 2026-10-12): an amount agreed for each day
 * worked, or for each week or fifteen days, is `tarifa`; a fixed amount every month is
 * `salarioMensual` alone.
 */
function pagoCompleto(v: { frecuencia: string; porDia: boolean; tarifa?: number | undefined }, ctx: z.RefinementCtx): void {
  const llevaTarifa = v.porDia || v.frecuencia !== 'mensual';
  if (llevaTarifa && v.tarifa === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tarifa'], message: 'Falta cuánto se le paga por día, por semana o por quincena' });
  }
  if (!llevaTarifa && v.tarifa !== undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tarifa'], message: 'Un sueldo fijo de cada mes es el salario mensual' });
  }
}

/**
 * A payment in the transfer queue: a sale's (`ventaId` with `indice`) or a top-up's
 * (`recargaId`), and never both.
 */
function unPagoEnCola(v: { ventaId?: string; indice?: number; recargaId?: string; abonoId?: string }, ctx: z.RefinementCtx): void {
  const deVenta = v.ventaId !== undefined && v.indice !== undefined;
  if ([deVenta, v.recargaId !== undefined, v.abonoId !== undefined].filter(Boolean).length !== 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Un pago en la cola es de una venta, de una recarga o de un abono' });
  }
}

/** A line of an order, as it is taken (`pedido.tomado`) and as it is changed (`pedido.actualizado`). */
const lineaDePedido = z.object({
  productoId: z.string().uuid(),
  unidadVentaId: z.string().uuid(),
  cantidad: z.number().int().positive(),
  precioUnitario: z.number().int().nonnegative(),
  subtotal: z.number().int().nonnegative(),
  /**
   * The list the line's price came from, when it is not the order's (the owner, 2026-10-18: «set
   * the retail/wholesale price toggle on each item like in the sale screen»): the case of oil at
   * wholesale and the rest at the client's list, as a sale's line says it (2026-10-11). Absent, the
   * order's `listaId`. The till rings the line up at it. A phone that has not updated drops it, and
   * keeps the line's price all the same.
   */
  listaId: z.string().uuid().optional(),
  /** Why the price agreed is not the list's, as on a sale's line. */
  motivoOverride: z.string().max(200).optional(),
  /**
   * The owner who allowed a price below the margin floor, or brought down with no cost
   * known: whose PIN was typed, or the owner taking the order. A sale's line means the same
   * by it; the sale is often rung by somebody else, so the order names the owner outright.
   */
  autorizadoPor: z.string().uuid().optional(),
});

/** A person's aguinaldo on a payment of it (`aguinaldo.pagado`), and a correction of one. */
const lineaDeAguinaldo = z.object({
  empleadoId: z.string().uuid(),
  /** What they are handed, in centavos: no INSS nor income tax comes off it. */
  monto: z.number().int().positive(),
});

/**
 * A person's line of a run: what a run sends, and a correction of it (`planilla.corregida`).
 */
const lineaDePlanilla = z.object({
  empleadoId: z.string().uuid(),
  /**
   * Salario ordinario de este período. Omitirlo usa el salario del empleado, que es lo
   * normal; enviarlo cubre el mes incompleto de quien entró a mitad de mes.
   */
  bruto: z.number().int().nonnegative().optional(),
  /** Horas extra, comisiones, bonos. Gravables. */
  otrosGravables: z.number().int().nonnegative().default(0),
  /** Vacaciones pagadas en este período. Gravables (Art. 76 CT). */
  vacacionesPagadas: z.number().int().nonnegative().default(0),
  /** Días de vacaciones que corresponden a ese pago, en milésimas de día. */
  diasVacaciones: z.number().int().nonnegative().default(0),
  /** Aguinaldo pagado en este período. Exento de INSS e IR (Art. 97 CT). */
  aguinaldoPagado: z.number().int().nonnegative().default(0),
  /** Days worked in it, in thousandths (five and a half: 5500), for somebody paid by the day. */
  dias: z.number().int().positive().optional(),
  /**
   * The most of what they owe in advances it takes off (2026-10-31): for a run written down
   * once it was paid, what they owed of those given by the day it was. One given after was
   * not taken off the money handed over that day, and waits for the next run. Absent, all
   * they owe, up to the net.
   */
  adelantoMaximo: z.number().int().nonnegative().optional(),
});

export const EVENT_SCHEMAS = {
  // ---------- Usuarios ----------
  'usuario.creado': z.object({
    usuarioId: z.string().uuid(),
    nombre: z.string().min(1).max(80),
    rol: z.enum(ROLES),
    pinHash: z.string().min(1),
  }),

  'usuario.actualizado': z.object({
    usuarioId: z.string().uuid(),
    nombre: z.string().min(1).max(80).optional(),
    rol: z.enum(ROLES).optional(),
  }),

  'usuario.pin_cambiado': z.object({
    usuarioId: z.string().uuid(),
    pinHash: z.string().min(1),
  }),

  'usuario.desactivado': z.object({
    usuarioId: z.string().uuid(),
    motivo: z.string().max(200).optional(),
  }),

  'usuario.reactivado': z.object({
    usuarioId: z.string().uuid(),
  }),

  // ---------- Sesiones ----------
  /**
   * Somebody got in with their PIN, at the sign-in pad or on a screen that had locked. The
   * actor is who got in. Kept for the bitácora; nothing is derived from it.
   */
  'sesion.iniciada': z.object({
    donde: z.enum(['entrada', 'desbloqueo']),
  }),

  /**
   * A wrong PIN. At the sign-in pad and on the lock screen the actor is the person whose PIN
   * was tried: nobody is signed in yet, and whose account somebody was trying is the fact
   * worth keeping. On a PIN sheet it is whoever is signed in, who typed it — into the owner's
   * authorisation, or to confirm something in their own name.
   */
  'sesion.pin_incorrecto': z.object({
    donde: z.enum(['entrada', 'desbloqueo', 'autorizacion', 'confirmacion']),
  }),

  // ---------- Dispositivos ----------
  'dispositivo.registrado': z.object({
    dispositivoId: z.string().uuid(),
    /** Two characters. Prefixes every ticket this device issues, so offline numbering cannot collide. */
    codigo: z.string().regex(/^[A-HJ-NP-Z2-9]{2}$/),
    nombre: z.string().min(1).max(60),
    tipo: z.enum(['mostrador', 'telefono', 'camion', 'admin']),
  }),

  'dispositivo.revocado': z.object({
    dispositivoId: z.string().uuid(),
    motivo: z.string().max(200).optional(),
  }),

  /**
   * A revoked device given back (the owner, 2026-10-06: «can we add something to unrevoke
   * devices»): the phone turned up, or the wrong one was revoked. It syncs again with its own
   * token, and what it had not sent goes in then; registering it again would have wiped that.
   */
  'dispositivo.reactivado': z.object({
    dispositivoId: z.string().uuid(),
    motivo: z.string().max(200).optional(),
  }),

  // ---------- Catálogo ----------

  /**
   * Creating a product also creates its first sale unit, and optionally its opening prices.
   *
   * One event, not three. A product with no sale unit cannot be priced or sold, so splitting
   * these would leave a moment where the catalog holds something unsellable — and the bulk
   * entry screen, which is about to create several hundred rows from a paper list, would queue
   * three events per row instead of one.
   */
  'producto.creado': z.object({
    productoId: z.string().uuid(),
    nombre: z.string().min(1).max(120),
    /**
     * The same product in English, for whoever reads the books in English. Typed by a person
     * — or shipped with the starter catalogue — and never machine-translated. `nombre` stays
     * what the counter says and what the receipt prints.
     */
    nombreEn: z.string().min(1).max(120).optional(),
    categoria: z.string().max(60).default('Otros'),
    /** Code of the unit stock is held in: 'unidad', 'libra', 'litro'. */
    unidadBase: z.string().min(1).max(20),
    esMedicamento: z.boolean().default(false),
    perecedero: z.boolean().default(false),
    /** MINSA registration. Relevant only for medicine on the venta-libre list. */
    registroSanitario: z.string().max(60).optional(),
    codigosBarras: z.array(z.string().min(1).max(CODIGO_MAX)).default([]),
    unidadVenta: z.object({
      unidadVentaId: z.string().uuid(),
      nombre: z.string().min(1).max(40),
      /** Base units in one of these, in milli. A 5 lb bag of a pound-based product is 5000. */
      factor: z.number().int().positive(),
    }),
    precios: z
      .array(z.object({ listaId: z.string().uuid(), precio: z.number().int().nonnegative() }))
      .default([]),
    /** SHA-256 of the product's photo. Only the name rides in the log; see `fotos.ts`. */
    foto: fotoHashSchema.optional(),
    /**
     * Stock, in base units (milli), at or below which it is time to buy more. Optional: without
     * one, «Por comprar» judges by how fast the product sells.
     */
    stockMinimo: z.number().int().nonnegative().optional(),
  }),

  'producto.actualizado': z.object({
    productoId: z.string().uuid(),
    nombre: z.string().min(1).max(120).optional(),
    /** `null` clears it, and the English screen falls back to `nombre`. */
    nombreEn: z.string().min(1).max(120).nullable().optional(),
    categoria: z.string().max(60).optional(),
    /**
     * Another unit for the stock, mostly to put right a product created in the wrong one. Only its
     * name changes: every number stays as it was counted (see `reduceProducto`).
     */
    unidadBase: z.string().min(1).max(20).optional(),
    esMedicamento: z.boolean().optional(),
    perecedero: z.boolean().optional(),
    registroSanitario: z.string().max(60).nullable().optional(),
    codigosBarras: z.array(z.string().min(1).max(CODIGO_MAX)).optional(),
    /** A new photo's SHA-256, or `null` to take the photo away. */
    foto: fotoHashSchema.nullable().optional(),
    /** In base units (milli); `null` takes the minimum away. */
    stockMinimo: z.number().int().nonnegative().nullable().optional(),
  }),

  'producto.desactivado': z.object({
    productoId: z.string().uuid(),
    motivo: z.string().max(200).optional(),
  }),

  'producto.reactivado': z.object({ productoId: z.string().uuid() }),

  'unidad_venta.creada': z.object({
    unidadVentaId: z.string().uuid(),
    productoId: z.string().uuid(),
    nombre: z.string().min(1).max(40),
    factor: z.number().int().positive(),
    /** A presentation's own barcodes: the case's code rings up the case, not one can. */
    codigosBarras: z.array(z.string().min(1).max(CODIGO_MAX)).default([]),
  }),

  'unidad_venta.actualizada': z.object({
    unidadVentaId: z.string().uuid(),
    nombre: z.string().min(1).max(40).optional(),
    factor: z.number().int().positive().optional(),
    codigosBarras: z.array(z.string().min(1).max(CODIGO_MAX)).optional(),
  }),

  'unidad_venta.eliminada': z.object({ unidadVentaId: z.string().uuid() }),

  // ---------- Precios ----------

  'lista_precio.creada': z.object({
    listaId: z.string().uuid(),
    nombre: z.string().min(1).max(40),
    /** The list applied to a walk-in customer with no assigned tier. */
    esPredeterminada: z.boolean().default(false),
    orden: z.number().int().nonnegative().default(0),
  }),

  'lista_precio.actualizada': z.object({
    listaId: z.string().uuid(),
    nombre: z.string().min(1).max(40).optional(),
    esPredeterminada: z.boolean().optional(),
    orden: z.number().int().nonnegative().optional(),
  }),

  'lista_precio.eliminada': z.object({ listaId: z.string().uuid() }),

  /** Price for one sale unit on one list. Centavos. */
  'precio.fijado': z.object({
    unidadVentaId: z.string().uuid(),
    listaId: z.string().uuid(),
    precio: z.number().int().nonnegative(),
  }),

  /**
   * Una oferta (2026-11-03, the owner: «a tool that suggests to me products that haven't been moving
   * so well … which products to put on sale and for how long, basically a promotion generator»): a
   * product's prices on one list, lower, from one day to another, both in. The till charges them as
   * that list's while they last (`ofertaDe` on the phone), and they end by themselves: nobody has to
   * remember to put the price back. A presentation not named keeps its price. One at a time for a
   * product on a list: the books refuse one whose days meet another's.
   */
  'oferta.creada': z
    .object({
      ofertaId: z.string().uuid(),
      productoId: z.string().uuid(),
      listaId: z.string().uuid(),
      /**
       * What each presentation it names gets: a lower price (`precio`), or so many for the price of
       * fewer (`lleva`, `paga`, 2026-11-03: «2×1», «lleva 3, paga 2»), charged at the list's price with
       * `lleva − paga` of every `lleva` free. One or the other; a presentation not named keeps its own.
       */
      precios: z
        .array(
          z
            .object({
              unidadVentaId: z.string().uuid(),
              precio: z.number().int().positive().optional(),
              lleva: z.number().int().min(2).max(50).optional(),
              paga: z.number().int().min(1).max(49).optional(),
            })
            .refine((x) => (x.precio === undefined) !== (x.lleva === undefined && x.paga === undefined), {
              message: 'Cada presentación lleva un precio o un «lleva y paga», uno de los dos',
            })
            .refine((x) => (x.lleva === undefined) === (x.paga === undefined), { message: 'Un «lleva y paga» dice cuántos se llevan y cuántos se pagan' })
            .refine((x) => x.lleva === undefined || x.paga! < x.lleva, { message: 'Se paga menos de lo que se lleva', path: ['paga'] }),
        )
        .min(1)
        .max(20),
      desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /** Why: what will expire before it sells, what is not moving, or the owner's own reason. */
      motivo: z.enum(['vence', 'lento', 'otro']),
      nota: z.string().max(300).optional(),
    })
    .refine((v) => v.desde <= v.hasta, { message: 'La oferta termina antes de empezar', path: ['hasta'] }),

  /**
   * An offer ended by hand before its last day (`fecha`, that day at the shop): from then on the till
   * charges the list's own price. What sold at the offer keeps its price.
   */
  'oferta.terminada': z.object({
    ofertaId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    nota: z.string().max(300).optional(),
  }),

  // ---------- Proveedores ----------

  'proveedor.creado': z.object({
    proveedorId: z.string().uuid(),
    nombre: z.string().min(1).max(120),
    telefono: z.string().max(40).optional(),
    contacto: z.string().max(80).optional(),
    notas: z.string().max(500).optional(),
  }),

  'proveedor.actualizado': z.object({
    proveedorId: z.string().uuid(),
    nombre: z.string().min(1).max(120).optional(),
    telefono: z.string().max(40).nullable().optional(),
    contacto: z.string().max(80).nullable().optional(),
    notas: z.string().max(500).nullable().optional(),
  }),

  'proveedor.desactivado': z.object({ proveedorId: z.string().uuid() }),

  // ---------- Compras e inventario ----------

  /**
   * A received purchase. Everything needed to move stock, recompute weighted-average cost and
   * post to the ledger is carried here, so a replay reproduces all three exactly.
   *
   * Lines carry a line TOTAL rather than a unit cost: that is what the supplier's invoice
   * actually says, and deriving the unit cost from the total avoids the rounding drift that
   * comes from multiplying a rounded unit cost back out.
   */
  'compra.registrada': z
    .object({
      compraId: z.string().uuid(),
      proveedorId: z.string().uuid(),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      documento: z.string().max(60).optional(),
      moneda: z.enum(['NIO', 'USD']),
      /** C$ per USD × 10,000, as of the purchase date. Required when moneda is USD. */
      tasaCambio: z.number().int().positive().optional(),
      /** Freight and other landed costs, in the purchase currency, spread across lines by value. */
      flete: z.number().int().nonnegative().default(0),
      /**
       * The invoice's IVA, in the purchase currency (the owner, 2026-10-11). Part of what the goods
       * cost: the shop charges no IVA, so what it pays it cannot get back. Spread across every line
       * by value, as freight is. Absent, none, as on every purchase before.
       */
      iva: z.number().int().nonnegative().default(0),
      ubicacion: ubicacionSchema,
      formaPago: z.enum([
        'efectivo_cordobas',
        'efectivo_dolares',
        'transferencia',
        'billetera',
        'credito',
      ]),
      /**
       * Paid out of the till open on this device, in córdobas: it leaves the drawer instead of
       * general cash, and that till's corte expects it gone. Only with `efectivo_cordobas`.
       */
      sesionId: z.string().uuid().optional(),
      /**
       * The bank account `formaPago` paid from, by transfer: a dollar account only for a purchase in
       * dollars, which it pays in its dollars, at the official rate as dollars from the safe do.
       */
      cuenta: cuentaBancoSchema.optional(),
      /** Due date when bought on terms. «Cuentas por pagar» in «Gastos y pagos» sorts by it. */
      vencePago: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      /**
       * Paid in parts, each its own way, when the goods came (the owner, 2026-10-04): part by
       * transfer and part in cash, or the trucker paid in cash for a supplier who gives credit.
       * `formaPago` pays what the parts leave, always something: on credit, what is left is owed.
       * Each part in the purchase's currency, like the lines. Absent, `formaPago` paid it all, as
       * every purchase did before.
       */
      pagos: z
        .array(
          z.object({
            pagoId: z.string().uuid(),
            formaPago: z.enum(['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera']),
            monto: z.number().int().positive(),
            /** Out of the till open on this device, as the purchase's own `sesionId`: córdobas in cash only. */
            sesionId: z.string().uuid().optional(),
            /** The córdoba account a part by transfer came from. */
            cuenta: cuentaDeTransferencia,
          }),
        )
        .min(1)
        .max(6)
        .optional(),
      lineas: z
        .array(
          z.object({
            productoId: z.string().uuid(),
            /** Quantity in BASE units, milli. The UI converts from whatever unit was typed. */
            cantidadBase: z.number().int().positive(),
            /** Line total in the purchase currency, excluding freight. Centavos. */
            costoTotal: z.number().int().nonnegative(),
            lote: z
              .object({
                numero: z.string().min(1).max(40),
                vencimiento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
              })
              .optional(),
          }),
        )
        .min(1),
    })
    .superRefine((v, ctx) => {
      if (v.moneda === 'USD' && !v.tasaCambio) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tasaCambio'],
          message: 'Una compra en dólares necesita la tasa de cambio del día',
        });
      }
      if (v.formaPago === 'credito' && !v.vencePago) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vencePago'],
          message: 'Una compra al crédito necesita fecha de vencimiento',
        });
      }
      if (v.pagos) {
        const total = v.lineas.reduce((a, l) => a + l.costoTotal, v.flete + v.iva);
        if (v.pagos.reduce((a, x) => a + x.monto, 0) >= total) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pagos'],
            message: 'Las partes pagan toda la compra: la última va en la forma de pago',
          });
        }
        if (new Set(v.pagos.map((x) => x.pagoId)).size !== v.pagos.length) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'Dos partes con el mismo pago' });
        }
        if (v.pagos.some((x) => x.sesionId && x.formaPago !== 'efectivo_cordobas')) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'De la gaveta solo sale efectivo en córdobas' });
        }
        if (v.pagos.some((x) => x.cuenta !== undefined && x.formaPago !== 'transferencia')) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'Solo una transferencia va a una cuenta de banco' });
        }
      }
      cuentaSoloConTransferencia(v, v.formaPago, ctx);
      dolaresSoloEnDolares(v, ctx);
    }),

  /**
   * Undo a purchase.
   *
   * Only possible while the purchase is still the most recent movement for every product it
   * touched — the reducer refuses otherwise. Weighted average is path-dependent, so once a sale
   * or a later purchase has moved the average, there is no honest way to subtract this one back
   * out. That case wants an adjustment, which is a new fact, not an erasure.
   */
  'compra.anulada': z.object({
    compraId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
    /**
     * Refused once the money it paid with was counted after it: out of a till, once that till was
     * handed over or closed; otherwise once general cash, the bank or the wallet was
     * (`cuentasContadas`). Sent by every void from this version on; one without it is from before,
     * and stands as it did then, so the log replays as it always has (as `gasto.anulado`).
     */
    sinContar: z.literal(true).optional(),
  }),

  /**
   * The cost typed on a purchase line was wrong — a case entered as units, a slipped digit — and
   * its goods have moved since, so it can no longer be voided. The correct cost changes what the
   * stock still on the shelf is worth and, for what already sold, the cost of those sales; what
   * was owed or paid for it changes with it.
   */
  'compra.costo_corregido': z.object({
    compraId: z.string().uuid(),
    /** The line, by position: one purchase can carry the same product twice. */
    indice: z.number().int().nonnegative(),
    /** The line's correct total, in the purchase's currency, freight and IVA apart: its share of them stays. */
    costoTotal: z.number().int().nonnegative(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * The quantity typed on a purchase line was wrong — a sack of a hundred pounds entered as one
   * pound — found once its goods have moved (the owner, 2026-10-13). What the line cost stays what it
   * cost; what changes is what each unit cost: on the shelf, and in the sales that took the
   * purchase's cost. Moves no money.
   */
  'compra.cantidad_corregida': z.object({
    compraId: z.string().uuid(),
    /** The line, by position, as `compra.costo_corregido`. */
    indice: z.number().int().nonnegative(),
    /** What really came, in base units. */
    cantidadBase: z.number().int().positive(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * A purchase written down as paid one way and paid another: cash that went by transfer, a
   * transfer that was cash, on credit when it was paid, paid when it was on credit (the owner,
   * 2026-10-14). What the goods cost stays; the money goes back where the purchase said it came
   * from, and comes out of where it did, as `compra.registrada` names it. Whole: a purchase paid in
   * parts, in dollars, with returns, or on credit with payments made on it is refused.
   */
  'compra.forma_pago_corregida': z
    .object({
      compraId: z.string().uuid(),
      formaPago: z.enum(['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera', 'credito']),
      /** Out of the till open when it came, as `compra.registrada`'s: córdobas in cash only. */
      sesionId: z.string().uuid().optional(),
      /** The córdoba account a transfer came from. */
      cuenta: cuentaDeTransferencia,
      /** When it is due, on credit. */
      vencePago: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      motivo: z.string().min(1).max(200),
    })
    .superRefine((v, ctx) => {
      if (v.formaPago === 'credito' && !v.vencePago) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vencePago'], message: 'Una compra al crédito necesita fecha de vencimiento' });
      }
      if (v.vencePago !== undefined && v.formaPago !== 'credito') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vencePago'], message: 'Solo una compra al crédito vence' });
      }
      if (v.sesionId !== undefined && v.formaPago !== 'efectivo_cordobas') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sesionId'], message: 'De la gaveta solo sale efectivo en córdobas' });
      }
      cuentaSoloConTransferencia(v, v.formaPago, ctx);
    }),

  /**
   * Goods going back to the supplier: damaged, expired, not what was ordered.
   *
   * The adjustment a purchase that can no longer be annulled needs. The goods leave at the
   * running average, like any other exit, so the average stays honest; the supplier credits
   * what the purchase paid for them, and any gap between the two is a correction to cost of
   * sales, which is where it came from.
   */
  'compra.devuelta': z.object({
    devolucionId: z.string().uuid(),
    compraId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lineas: z
      .array(
        z.object({
          /** Position of the line in the purchase. */
          indice: z.number().int().nonnegative(),
          /** In base units, milli. At most what came in, less what already went back. */
          cantidadBase: z.number().int().positive(),
        }),
      )
      .min(1),
    /**
     * `descontar` takes it off what is still owed on this purchase, which has to be on credit;
     * the rest is money the supplier hands back.
     */
    liquidacion: z.enum(['descontar', 'efectivo_cordobas', 'transferencia', 'billetera']),
    /** The córdoba account the supplier's money came back to, by transfer. */
    cuenta: cuentaDeTransferencia,
    motivo: z.string().min(1).max(200),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.liquidacion, ctx)),

  /**
   * Opening stock for a business that already has goods on the shelf.
   *
   * Posts against Capital, not against an expense: the owner contributed this inventory, they
   * did not spend it. Sets an absolute quantity and cost rather than a delta, because what is
   * being recorded is the result of a count.
   */
  'inventario.saldo_inicial': z.object({
    productoId: z.string().uuid(),
    ubicacion: ubicacionSchema,
    cantidadBase: z.number().int().nonnegative(),
    /** Thousandths of a centavo per base unit. */
    costoUnitario: z.number().int().nonnegative(),
  }),

  /**
   * What one unit of a product costs in one place, set right by the owner (2026-10-13): its average
   * cost was wrong — goods counted with no cost, a purchase's cost mixed with later ones — and from
   * now on the stock is worth what this says. The quantity stays; what the stock's worth changes by
   * goes to «Ajuste del costo de inventario». Sales already made keep what they cost.
   */
  'inventario.costo_corregido': z.object({
    productoId: z.string().uuid(),
    ubicacion: ubicacionSchema,
    /** Thousandths of a centavo per base unit, as `inventario.saldo_inicial`. */
    costoUnitario: z.number().int().nonnegative(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * A supplier's invoice still unpaid from before the shop kept its books here, one per invoice.
   *
   * It becomes a purchase on credit with no goods: those came in long ago, and are in the opening
   * stock. It goes against Capital, as opening stock does, and from then on it sits in «Cuentas
   * por pagar» and is paid off like any purchase on credit (`compra.pagada`).
   *
   * Sets it rather than adding to it. Sent again for the same `compraId` it corrects the amount,
   * the supplier, the invoice or its dates, and only the difference in the amount is posted. Zero
   * takes it back off, while nothing has been paid on it.
   */
  'compra.saldo_inicial': z.object({
    compraId: z.string().uuid(),
    proveedorId: z.string().uuid(),
    /** The invoice's date. */
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    documento: z.string().max(60).optional(),
    /** What is still owed on it, in córdobas. Centavos. */
    monto: z.number().int().nonnegative(),
    vencePago: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  }),

  // ---------- Clientes ----------

  'cliente.creado': z.object({
    clienteId: z.string().uuid(),
    nombre: z.string().min(1).max(120),
    tipo: z.enum(['mostrador', 'mayorista']).default('mostrador'),
    telefono: z.string().max(40).nullable().default(null),
    /**
     * Most of Nicaragua has no street addresses. "De la iglesia, 2 cuadras al lago" is how
     * people actually give directions, so this is free text and the landmark matters more
     * than anything resembling a street name.
     */
    direccion: z.string().max(300).nullable().default(null),
    /** Price tier. Falls back to the default list when unset. */
    listaId: z.string().uuid().nullable().default(null),
    /** Day the route passes: 0 = domingo. */
    diaVisita: z.number().int().min(0).max(6).nullable().default(null),
    notas: z.string().max(500).nullable().default(null),
    /**
     * Fiado: the most this client may owe without the owner, in centavos. Null is a limit of
     * nothing: any fiado then takes the owner, selling or by PIN. The owner decides who buys on
     * credit (`dar_credito`); the cashier only applies the decision.
     */
    limiteCredito: z.number().int().nonnegative().nullable().default(null),
    /**
     * Their ID, for somebody the shop is about to trust with fiado: the cédula's number as
     * typed, and a picture of the card. The picture travels like a product's, by its name.
     */
    cedula: z.string().max(30).nullable().default(null),
    fotoCedula: fotoHashSchema.nullable().default(null),
  }),

  'cliente.actualizado': z.object({
    clienteId: z.string().uuid(),
    nombre: z.string().min(1).max(120).optional(),
    tipo: z.enum(['mostrador', 'mayorista']).optional(),
    telefono: z.string().max(40).nullable().optional(),
    direccion: z.string().max(300).nullable().optional(),
    listaId: z.string().uuid().nullable().optional(),
    diaVisita: z.number().int().min(0).max(6).nullable().optional(),
    notas: z.string().max(500).nullable().optional(),
    /** `null` ends fiado for this client. What they already owe stays owed. */
    limiteCredito: z.number().int().nonnegative().nullable().optional(),
    cedula: z.string().max(30).nullable().optional(),
    fotoCedula: fotoHashSchema.nullable().optional(),
  }),

  'cliente.desactivado': z.object({ clienteId: z.string().uuid() }),

  /**
   * What a client already owed when the shop started keeping fiado here: the notebook's figure,
   * carried over. It goes against Capital, as opening stock does: the owner was owed it already,
   * and no sale here made it. Abonos pay it off like any fiado.
   *
   * Sets it rather than adding to it, so typing it again corrects it: only the difference moves
   * what they owe, and the books.
   */
  'cliente.saldo_inicial': z.object({
    clienteId: z.string().uuid(),
    /** Centavos. Zero takes it back off. */
    monto: z.number().int().nonnegative(),
  }),

  // ---------- Preventa ----------

  /**
   * An order taken now to be delivered later.
   *
   * Records prices as agreed at the moment of the visit, because that is what the customer was
   * told and what they will expect at the door. Moves no stock: a promise is not a movement. With
   * `apartarPedidos` on (2026-10-08) it sets aside what it takes, and is refused past what is not
   * set aside already; nothing is stored for it, the pending orders are what is set aside.
   *
   * A price agreed is held to the till's rules (the owner, 2026-10-05): changed from the list it
   * says why, and below the margin floor it says which owner allowed it. The till carries both to
   * the sale that delivers the order, so an order is not a way round what the counter would ask.
   */
  'pedido.tomado': z.object({
    pedidoId: z.string().uuid(),
    clienteId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    fechaEntrega: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    listaId: z.string().uuid(),
    lineas: z.array(lineaDePedido).min(1),
    /** What the lines come to: the goods alone, without `envio`. */
    total: z.number().int().nonnegative(),
    /**
     * What the customer pays for having it brought (the owner, 2026-10-12: typed when the order is
     * taken, «an optional field above total»), apart from `total`, which a phone that has not
     * updated still finds adding up. The till starts the delivering sale's own `envio` at it. Sent
     * only when there is one.
     */
    envio: z.number().int().nonnegative().optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * An order still to deliver, changed (the owner, 2026-10-15: «We need to be able to edit orders in
   * the order screen»): who it is for, the day, the lines, the delivery and the note, as it now
   * stands, every field of it, so nothing is left of what it said before. The day it was taken
   * stays. Its lines are held to the same rules as when it was taken: a price changed says why, and
   * below the margin floor it names the owner who allowed it.
   *
   * Refused once the order has been delivered or cancelled: what was delivered is a sale, and is
   * put right as a sale is. With `apartarPedidos` on, it is refused past what is not set aside only
   * for what it asks more of than it held: an order asking for less, or for the same on another
   * day, is never turned away.
   */
  'pedido.actualizado': z.object({
    pedidoId: z.string().uuid(),
    clienteId: z.string().uuid(),
    fechaEntrega: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    listaId: z.string().uuid(),
    lineas: z.array(lineaDePedido).min(1),
    /** What the lines come to: the goods alone, without `envio`. */
    total: z.number().int().nonnegative(),
    /** What the customer pays for having it brought; zero for none. */
    envio: z.number().int().nonnegative(),
    nota: z.string().max(300).nullable(),
  }),

  'pedido.cancelado': z.object({
    pedidoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  // ---------- Reparto ----------

  /**
   * A route run: one driver, one vehicle, one day.
   *
   * Exists to make the two halves of settlement inseparable. Cash and goods are both the
   * driver's responsibility, and closing one without the other is how a shop discovers in
   * March that the truck has been quietly short since November.
   */
  'jornada.abierta': z.object({
    jornadaId: z.string().uuid(),
    /** The vehicle's stock location: "camion:<uuid>". */
    camion: ubicacionSchema.refine((u) => u.startsWith('camion:'), 'Una jornada necesita un camión'),
    repartidorId: z.string().uuid(),
    /** The driver's own caja, opened for this vehicle. */
    sesionId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),

  /**
   * Liquidación: one atomic act that reconciles both cash and goods.
   *
   * Counts what is still on the truck, counts the cash handed in, writes off the difference on
   * each side, returns whatever is left to the storeroom and closes the driver's caja. Doing it
   * as one event is the point — a settlement that closes the money and leaves the goods
   * unreconciled is not a settlement.
   */
  'jornada.liquidada': z.object({
    jornadaId: z.string().uuid(),
    /** What the driver and the warehouse counted together, coming off the truck. */
    conteoCamion: z
      .array(
        z.object({
          productoId: z.string().uuid(),
          cantidad: z.number().int().nonnegative(),
        }),
      )
      .default([]),
    /** Everything the driver hands in, float included: it all goes to general cash. */
    conteoEfectivo: z.number().int().nonnegative(),
    conteoDolares: z.number().int().nonnegative().default(0),
    /** The rate the screen showed. The books use the official rate, as at any corte. */
    tasaCambio: z.number().int().positive(),
    /** Where unsold product goes back to. */
    ubicacionRetorno: ubicacionSchema.default('bodega'),
    nota: z.string().max(500).optional(),
  }),

  // ---------- Movimientos de inventario ----------

  /**
   * Reempaque: open bulk, produce packaged goods.
   *
   * Source and destinations are DIFFERENT products — a quintal of "Arroz a granel" becoming
   * hundred bags of "Arroz 1 lb", each with its own barcode and price. Splitting one product
   * into its own sale units is not a repackaging; that is just a unit conversion and moves
   * nothing.
   *
   * Cost follows the goods: the value consumed, plus whatever the bags and the labour cost,
   * lands on the output. Product explicitly lost on the way is expensed instead, which is the
   * only way the shop ever finds out that bagging rice costs it a pound a sack.
   */
  'inventario.reempaque': z
    .object({
      reempaqueId: z.string().uuid(),
      ubicacion: ubicacionSchema,
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      origenProductoId: z.string().uuid(),
      /** Total consumed from the source, in ITS base units. Includes anything lost. */
      origenCantidad: z.number().int().positive(),
      /** The part of the consumed quantity that did not become product. */
      mermaCantidad: z.number().int().nonnegative().default(0),
      destinos: z
        .array(
          z.object({
            productoId: z.string().uuid(),
            /** Produced quantity, in the DESTINATION product's base units. */
            cantidad: z.number().int().positive(),
            /**
             * How much of the source went into it, in the SOURCE's base units (2026-10-13): what its
             * cost is shared out by. Two bag sizes out of one sack are fifty bags of a pound and a
             * hundred of half a pound, the same rice each; shared by the number of bags, a half-pound
             * bag cost as much as a pound one. Given for every destination or for none: then the
             * quantities, which say it when they are in the source's own unit.
             */
            consumido: z.number().int().positive().optional(),
          }),
        )
        .min(1),
      /** Bags, labels, labour. Paid out of general cash. */
      costoAdicional: z.number().int().nonnegative().default(0),
      nota: z.string().max(300).optional(),
    })
    .superRefine((v, ctx) => {
      if (v.mermaCantidad > v.origenCantidad) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mermaCantidad'],
          message: 'La merma no puede superar lo que se consumió',
        });
      }
      if (v.destinos.some((d) => d.productoId === v.origenProductoId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['destinos'],
          message: 'El producto de destino no puede ser el mismo que el de origen',
        });
      }
      const conConsumido = v.destinos.filter((d) => d.consumido !== undefined);
      if (conConsumido.length > 0 && conConsumido.length < v.destinos.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['destinos'], message: 'Lo que lleva de origen se dice de cada destino, o de ninguno' });
      }
      if (
        conConsumido.length === v.destinos.length &&
        conConsumido.reduce((a, d) => a + d.consumido!, 0) !== v.origenCantidad - v.mermaCantidad
      ) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['destinos'], message: 'Lo que lleva cada destino suma lo que se consumió, menos la merma' });
      }
    }),

  /**
   * Conteo físico.
   *
   * Records what was counted, absolute, not a delta. The difference against the books is
   * derived — and split into two accounts on purpose: product that went missing is not the same
   * fact as product that was damaged, and a shop that cannot tell them apart cannot tell
   * shrinkage from theft.
   */
  'inventario.conteo': z.object({
    conteoId: z.string().uuid(),
    ubicacion: ubicacionSchema,
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lineas: z
      .array(
        z.object({
          productoId: z.string().uuid(),
          cantidadContada: z.number().int().nonnegative(),
          /**
           * What the books said on the counting device when this line was counted. The count
           * applies the difference, so a sale rung up after the line was counted still counts
           * instead of being put back on the shelf by a number written down before it.
           */
          cantidadSistema: z.number().int(),
          /**
           * When this line was counted, on the device's clock. A movement from before it that
           * reaches the books later — a phone that sold with no signal — is already in what was
           * counted.
           */
          contadoEn: z.string().datetime({ offset: true }),
        }),
      )
      .min(1),
    nota: z.string().max(300).optional(),
  }),

  /**
   * Salida sin venta.
   *
   * Product leaving for a reason that is not a sale. Without this, inventory and the ledger
   * drift apart permanently: the goods are gone from the shelf and still on the books forever.
   */
  'inventario.salida': z.object({
    salidaId: z.string().uuid(),
    ubicacion: ubicacionSchema,
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    productoId: z.string().uuid(),
    cantidadBase: z.number().int().positive(),
    motivo: z.enum(['merma', 'vencido', 'consumo_interno', 'robo']),
    /** Set when writing off a specific expired batch. */
    loteId: z.string().max(200).optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * Traslado between locations.
   *
   * Goods move at the origin's average cost and blend into the destination's. Value only moves
   * between accounts when the locations map to different ones — shop floor to storeroom is the
   * same account and posts nothing.
   */
  'inventario.traslado': z.object({
    trasladoId: z.string().uuid(),
    origen: ubicacionSchema,
    destino: ubicacionSchema,
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lineas: z
      .array(
        z.object({
          productoId: z.string().uuid(),
          cantidadBase: z.number().int().positive(),
        }),
      )
      .min(1),
    nota: z.string().max(300).optional(),
  }),

  // ---------- Caja ----------

  /**
   * Opening a shift. The float is a COUNT, not a carry-over from last night — whoever opens
   * counts the drawer and types what is actually in it. What last night's corte left is what the
   * count is held against, not what gets typed.
   *
   * Each currency is counted on its own. A drawer that opens with US$ 20 among the córdobas
   * has to say so here, or the corte finds twenty dollars nobody expected and books them as a
   * gain.
   */
  'caja.abierta': z.object({
    sesionId: z.string().uuid(),
    ubicacion: ubicacionSchema,
    fondoInicial: z.number().int().nonnegative(),
    /** US$ in the drawer at opening, in USD cents. Absent before dollars could be counted in. */
    fondoDolares: z.number().int().nonnegative().default(0),
    /**
     * The float, on the books. `esperado` is what the last corte at this drawer left in it
     * (`relevoDe`'s `queda`), as this device knew it; `deGeneral`, córdobas put in from general
     * cash on top. Any dollars counted come from general cash too, since the corte takes every
     * dollar out. The count is held against `esperado` plus `deGeneral`, and the difference posts
     * as over or short, the same as at a corte. With neither, nothing was handed on: the whole
     * count is a float brought from general cash.
     */
    relevoDe: z.string().uuid().optional(),
    esperado: z.number().int().nonnegative().optional(),
    deGeneral: z.number().int().nonnegative().optional(),
    /**
     * The córdobas of a first count that was off by more than `cajaReconteoDesde`, after which the
     * drawer was counted again and `fondoInicial` is that second count. Kept for the owner, who
     * sees both: a recount that found the money is a miscount, one that found it only the second
     * time is worth a question. Absent when the first count stood.
     */
    primerConteo: z.number().int().nonnegative().optional(),
  }),

  /**
   * Cash taken out mid-shift (to the safe, to pay a supplier) or put in, in either currency: the
   * dollars leave the drawer as dollars, so the corte stops expecting them.
   */
  'caja.movimiento': z.object({
    sesionId: z.string().uuid(),
    tipo: z.enum(['retiro', 'ingreso']),
    /** In the cents of `moneda`: USD cents for dollars. */
    monto: z.number().int().positive(),
    moneda: z.enum(['NIO', 'USD']).default('NIO'),
    motivo: z.string().min(1).max(200),
    /** For a retiro only. */
    /**
     * The owner who took the money in hand as it left the drawer: their PIN, or the owner
     * doing it. Without one it waits in «Efectivo por recibir», on this till, until the owner
     * counts it (`efectivo.recibido`); what is short then is the till's. Ignored if it names
     * somebody who is not an active owner, so a slip here never blocks the till.
     */
    recibidoPor: z.string().uuid().optional(),
  }),

  /**
   * The drawer changes hands without closing: whoever takes over counts it blind. What the count
   * finds over or short belongs to the shift of whoever had it, and the drawer goes on from what
   * was counted, in the name of whoever counted. The actor is the person taking over.
   */
  'caja.entregada': z.object({
    sesionId: z.string().uuid(),
    conteoCordobas: z.number().int().nonnegative(),
    conteoDolares: z.number().int().nonnegative().default(0),
    nota: z.string().max(200).optional(),
    /** A first count that was off and was redone, each currency on its own (see `caja.abierta`). */
    primerConteoCordobas: z.number().int().nonnegative().optional(),
    primerConteoDolares: z.number().int().nonnegative().optional(),
  }),

  /**
   * Corte de caja.
   *
   * The counted figures arrive here; the system works out what it expected and records the
   * difference. The UI does not show the expected total until the count is entered — a blind
   * count is the only kind that measures anything.
   */
  'caja.cerrada': z.object({
    sesionId: z.string().uuid(),
    conteoCordobas: z.number().int().nonnegative(),
    conteoDolares: z.number().int().nonnegative(),
    /**
     * The rate the screen valued the counted dollars at. The books use the official rate from the
     * settings, which is what the drawer's dollars are carried at.
     */
    tasaCambio: z.number().int().positive(),
    notas: z.string().max(500).optional(),
    /**
     * Córdobas left in the drawer for the next shift: the float. The rest of the day's money —
     * the córdobas over it and every dollar — goes to general cash. Absent: nothing is left.
     */
    queda: z.number().int().nonnegative().default(0),
    /**
     * The owner who took the money in hand as it left the drawer: their PIN, or the owner
     * doing it. Without one it waits in «Efectivo por recibir», on this till, until the owner
     * counts it (`efectivo.recibido`); what is short then is the till's. Ignored if it names
     * somebody who is not an active owner, so a slip here never blocks the till.
     */
    recibidoPor: z.string().uuid().optional(),
    /**
     * A first count that was off by more than `cajaReconteoDesde`, in córdobas, or by any dollar,
     * and was redone: the counts above are the second ones. Each currency on its own, present only
     * when that one was counted again (see `caja.abierta`).
     */
    primerConteoCordobas: z.number().int().nonnegative().optional(),
    primerConteoDolares: z.number().int().nonnegative().optional(),
  }),

  /**
   * An opening's count put right by the owner (2026-10-14: «is there any way to correct that till
   * opening and till closing amount as the owner?»): what was really in the drawer when the shift
   * opened. The float brought from general cash, the opening's own difference and, once the till
   * has closed, what its corte found over or short follow it (`corregirApertura` in core). The
   * opening stays in the log as it was typed.
   */
  'caja.apertura_corregida': z.object({
    /** The opening (`caja.abierta`). */
    eventoId: z.string().uuid(),
    /** Its corte (`caja.cerrada`), once the till has closed: what that count found moves with it. */
    corteId: z.string().uuid().optional(),
    fondoInicial: z.number().int().nonnegative(),
    /** USD cents. */
    fondoDolares: z.number().int().nonnegative().default(0),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * A corte's count put right by the owner: what was really counted, and what was really left in
   * the drawer. What it found over or short, and what went to general cash or waits to be
   * received, follow it (`corregirCorte` in core). The corte stays in the log as it was typed.
   */
  'caja.corte_corregido': z.object({
    /** The corte (`caja.cerrada`). */
    eventoId: z.string().uuid(),
    /**
     * The opening of the shift that took over the drawer since (`caja.abierta`, `relevoDe` this
     * till), once one has: it was held against what was left, and moves with it (2026-10-15).
     * Without it, what was left is not corrected once the drawer has opened again.
     */
    aperturaSiguienteId: z.string().uuid().optional(),
    conteoCordobas: z.number().int().nonnegative(),
    /** USD cents. */
    conteoDolares: z.number().int().nonnegative().default(0),
    /** Córdobas really left in the drawer for the next shift: never more than counted. */
    queda: z.number().int().nonnegative(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * The owner receiving what a till handed over, counted: the corte's takeout and any retiro
   * nobody took in hand at the time. It goes to general cash as counted, and what is over or
   * short against what the till handed over is the till's — the cashier's, in the bitácora.
   */
  'efectivo.recibido': z.object({
    sesionId: z.string().uuid(),
    contadoCordobas: z.number().int().nonnegative(),
    /** USD cents. */
    contadoDolares: z.number().int().nonnegative().default(0),
    nota: z.string().max(300).optional(),
    /**
     * A first count that was off and was redone (`cajaReconteoDesde`), each currency on its own:
     * the counts above are the second ones. Kept for the owner to see, as at the till.
     */
    primerConteoCordobas: z.number().int().nonnegative().optional(),
    primerConteoDolares: z.number().int().nonnegative().optional(),
  }),

  /**
   * Counting general cash — the safe, and whatever else is not in a drawer — against the books.
   * Dollars count at the official rate, which is what general cash carries them at. What is
   * over or short posts to «Sobrante / Faltante de efectivo general»: nobody's till, so its own
   * accounts.
   */
  'efectivo.arqueo': z.object({
    contadoCordobas: z.number().int().nonnegative(),
    /** USD cents. */
    contadoDolares: z.number().int().nonnegative().default(0),
    /** What the screen showed as expected, in córdobas. For the record; the books use their own. */
    esperado: z.number().int().optional(),
    nota: z.string().max(300).optional(),
    /**
     * The safe's first count: what it holds is where the books start. The books never knew what
     * was in it when the shop began here, so the difference is capital the owner brought, not a
     * surplus earned. Every later count's difference is a surplus or a shortage.
     */
    inicial: z.boolean().optional(),
    /** A first count that was off and was redone, each currency apart: the counts above are the second. */
    primerConteoCordobas: z.number().int().nonnegative().optional(),
    primerConteoDolares: z.number().int().nonnegative().optional(),
  }),

  /**
   * A bank account or the mobile wallet squared with its statement: the balance it shows, on a
   * date. The gap against the books — a fee, a transfer that never came — posts to «Diferencias de
   * conciliación». Each bank account on its own (`bancos.ts`): every one has a statement of its own.
   */
  'cuenta.conciliada': z.object({
    cuenta: z.enum([...CUENTAS_BANCO, 'BILLETERA_MOVIL']),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /**
     * In córdobas: what the statement is carried at on the books. For the dollar account, what the
     * books carried it at, and the statement's dollars over or under theirs at the official rate:
     * each deposit went on converted on its own, so the statement converted whole would not match.
     */
    saldo: z.number().int(),
    /**
     * A dollar account's statement as it reads, in USD cents: what the account holds from then on.
     * A dollar account's is refused without it.
     */
    saldoDolares: z.number().int().optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * Cash taken from general cash to the bank: córdobas to a córdoba account, dollars to a dollar
   * account, at the official rate, which is what general cash carries them at. Before this a
   * deposit could not be written down: general cash came up short at its count and the bank over
   * at its statement.
   */
  'efectivo.depositado': z.object({
    depositoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    cordobas: z.number().int().nonnegative().default(0),
    /** USD cents. */
    dolares: z.number().int().nonnegative().default(0),
    /** The córdoba account the córdobas went to (`bancos.ts`); absent, «Bancos». */
    cuenta: cuentaBancoCordobasSchema.optional(),
    /** The dollar account the dollars went to; absent, «Banco en dólares». */
    cuentaDolares: cuentaBancoDolaresSchema.optional(),
    /** The deposit slip's number. */
    referencia: z.string().max(80).optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * Cash brought from the bank to general cash: a deposit the other way (the owner, 2026-10-07: «a
   * way to transfer cash from general cash to bank and vice versa»). Córdobas out of the córdoba
   * account, dollars out of the dollar account at the official rate, into general cash. Kept with
   * the deposits, marked as coming from the bank (`delBanco`), and voided as one is
   * (`efectivo.deposito_anulado`), which puts it back the other way. A kind of its own, since a
   * deposit read by a phone that has not updated would move the money the wrong way: such a phone
   * drops this one instead, so the phones are updated before the first.
   */
  'efectivo.sacado_del_banco': z.object({
    /** A deposit's id: it is kept, and voided, with them. */
    depositoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    cordobas: z.number().int().nonnegative().default(0),
    /** USD cents. */
    dolares: z.number().int().nonnegative().default(0),
    /** The córdoba account the córdobas came out of; absent, «Bancos». */
    cuenta: cuentaBancoCordobasSchema.optional(),
    /** The dollar account the dollars came out of; absent, «Banco en dólares». */
    cuentaDolares: cuentaBancoDolaresSchema.optional(),
    /** The withdrawal slip's or the cheque's number. */
    referencia: z.string().max(80).optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * A deposit that was not, voided: what it took to the bank is back in general cash. Not once
   * general cash was counted after it, or the account it went to was squared with its statement
   * (`cuentasContadas`): that count already found the difference.
   */
  'efectivo.deposito_anulado': z.object({
    depositoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * Money moved from one bank account to another (`bancos.ts`, 2026-10-10): a wire from the account
   * in the United States to the one in Nicaragua, dollars sold to the bank for córdobas. Between two
   * accounts in the same currency what left one is what reached the other; fees, if any, the next
   * statement finds. Between currencies, what reached the other is said too, and what it came to
   * against the official rate is a gain or a loss on the exchange.
   */
  'banco.transferido': z
    .object({
      transferenciaId: z.string().uuid(),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      desde: cuentaBancoSchema,
      hacia: cuentaBancoSchema,
      /** What left `desde`, in its currency: córdoba centavos or USD cents. */
      monto: z.number().int().positive(),
      /** What reached `hacia`, in its currency, when that is another: needed then, refused otherwise. */
      recibido: z.number().int().positive().optional(),
      referencia: z.string().max(80).optional(),
      nota: z.string().max(300).optional(),
    })
    .superRefine((v, ctx) => {
      if (v.desde === v.hacia) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hacia'], message: 'Una transferencia va a otra cuenta' });
      }
      const cambia = esCuentaEnDolares(v.desde) !== esCuentaEnDolares(v.hacia);
      if (cambia && v.recibido === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recibido'], message: 'Falta cuánto llegó en la otra moneda' });
      }
      if (!cambia && v.recibido !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recibido'], message: 'Entre cuentas de la misma moneda llega lo que sale' });
      }
    }),

  /**
   * A transfer between accounts that was not, voided: what left one goes back to it. Not once
   * either account was squared with a statement after it (`cuentasContadas`): that one found it.
   */
  'banco.transferencia_anulada': z.object({
    transferenciaId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * The owner has gone over the week's money (`/dinero/semana`): the tills' envelopes received,
   * what left the safe written down, the safe counted, the transfers and the statements seen to.
   * Nothing on the books changes; the home screen asks again a week after `hasta`.
   */
  'semana.cuadrada': z.object({
    /** The first and last days it went over, Nicaraguan dates. */
    desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),

  // ---------- Ventas ----------

  /**
   * A sale.
   *
   * Carries everything needed to reproduce it: the prices charged AND the list prices they
   * came from, the cost of each line at the moment it sold, and every payment. Snapshotting
   * cost here is what lets a reversal put stock back at the value it left with, and what makes
   * a margin report for last March mean anything.
   */
  'venta.registrada': z.object({
    ventaId: z.string().uuid(),
    /** Device-prefixed: "A1-000042". Two offline terminals cannot collide. */
    ticket: z.string().regex(/^[A-HJ-NP-Z2-9]{2}-\d{6,}$/),
    sesionId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ubicacion: ubicacionSchema,
    listaId: z.string().uuid(),
    clienteId: z.string().uuid().nullable().default(null),
    /** Set when this sale is the delivery of a preventa order. */
    pedidoId: z.string().uuid().nullable().default(null),

    lineas: z
      .array(
        z.object({
          /** Null for a venta rápida: something sold before it existed in the catalog. */
          productoId: z.string().uuid().nullable(),
          descripcion: z.string().min(1).max(120),
          unidadVentaId: z.string().uuid().nullable(),
          /** Quantity in SALE units, milli. */
          cantidad: z.number().int().positive(),
          /** Base units per sale unit, snapshotted so a later unit edit cannot rewrite history. */
          factor: z.number().int().positive(),
          /** What the price list said. */
          precioLista: z.number().int().nonnegative(),
          /**
           * The list `precioLista` came from, when it is not the sale's (2026-10-11): a till chooses
           * each line's list (the owner: «choose for each item on the screen»), the case of oil at
           * wholesale and the rest at retail. Absent, the sale's `listaId`. A phone that has not
           * updated drops it, and has the line's prices all the same.
           */
          listaId: z.string().uuid().optional(),
          /** What was actually charged. */
          precioUnitario: z.number().int().nonnegative(),
          motivoOverride: z.string().max(200).optional(),
          /**
           * Owner who authorised going below the margin floor; on a venta rápida line (no
           * product), who let the ticket's quick sales past «Venta rápida sin el dueño».
           */
          autorizadoPor: z.string().uuid().optional(),
          /**
           * The offer `precioLista` came from (`oferta.creada`, 2026-11-03): while it lasts it is the
           * list's price, and `precioRegular` the list's own without it. What an offer sold is told
           * apart by these, and «Por comprar» leaves it out of a product's pace. A phone that has not
           * updated drops them, and has the line's prices all the same.
           */
          ofertaId: z.string().uuid().optional(),
          precioRegular: z.number().int().nonnegative().optional(),
          /**
           * What this device believed the cost per BASE unit was, used to evaluate the margin
           * and decide whether an override needed the owner's PIN.
           *
           * NOT what gets posted. The reducer books COGS at the average in effect at this
           * event's position in the log, so a replay is self-correcting and a terminal that
           * had not yet synced a purchase cannot permanently poison the cost of sales.
           */
          costoReferencia: z.number().int().nonnegative(),
          subtotal: z.number().int(),
        }),
      )
      .min(1),

    /**
     * Owner whose PIN let the sale use a price list that is neither the default nor the client's own:
     * for the whole sale, or for some of its lines (`lineas[].listaId`).
     */
    listaAutorizadaPor: z.string().uuid().optional(),
    /**
     * Owner whose PIN let the sale take stock set aside for orders (`apartarPedidos`, 2026-10-08), or
     * the owner ringing it. Never refused: the goods left; the orders it took from are short, and
     * say so. A phone that has not updated drops it.
     */
    apartadoAutorizadoPor: z.string().uuid().optional(),
    /**
     * Sent by a till that could reach the server, with `venderSoloLoQueHay` on (2026-10-08): refuse
     * this sale if a product it takes is not free (the shop's stock less what pending orders set
     * aside, its own order aside). The server's order decides between two tills, and the till hears
     * the answer before it prints the receipt. Without it a sale is never refused for its stock.
     */
    soloSiHay: z.literal(true).optional(),
    /** The owner whose yes let the sale take more than was free: their PIN, or the owner ringing it. */
    faltaAutorizadaPor: z.string().uuid().optional(),
    /**
     * The copy a till records when the server did not answer in time (`soloSiHay`): the customer has
     * gone with the goods. If the sale stood, this is nothing; if the server refused it, this stands
     * in its place, unchecked. Same sale, same id, and always after it in the device's order.
     */
    respaldo: z.literal(true).optional(),
    /** Ticket-level discount, already reflected in `total`. */
    descuento: z.number().int().nonnegative().default(0),
    /**
     * What the customer pays for having their goods brought to them (the owner, 2026-10-05), already
     * in `total`, and booked as the shop's income for the trip («Cobros por envío»), apart from what
     * the goods sold for. Typed above the till's total, on any sale, an order's starting at what the
     * order says (2026-10-12; it was asked in «Cobrar», for orders only). Sent only when there is
     * one: a phone too old to know it reads such a sale's total as not adding up.
     */
    envio: z.number().int().nonnegative().default(0),
    /** Cash rounding adjustment. Positive means the customer paid up to the coin step. */
    redondeo: z.number().int().default(0),
    total: z.number().int(),

    pagos: z
      .array(
        z.object({
          forma: z.enum([
            'efectivo_cordobas',
            'efectivo_dolares',
            'transferencia',
            'billetera',
            'tarjeta',
            /** Fiado: the client takes the goods now and owes this part. Needs `clienteId`. */
            'credito',
          ]),
          /** Value applied to the bill, in córdobas. */
          monto: z.number().int().positive(),
          /** Foreign amount tendered, when paid in dollars. */
          montoOriginal: z.number().int().positive().optional(),
          /** Store rate used to credit the dollars. */
          tasaCambio: z.number().int().positive().optional(),
          referencia: z.string().max(80).optional(),
          /** Owner who let this fiado take the client past their limit. */
          autorizadoPor: z.string().uuid().optional(),
          /**
           * Transfers and wallet payments land as `pendiente`: a screenshot is not a payment.
           * They sit in a confirmation queue and post to a holding account until somebody
           * verifies the money.
           */
          estado: z.enum(['confirmado', 'pendiente']).default('confirmado'),
          /**
           * The córdoba account a card payment, or a transfer confirmed as it was taken, went to
           * (`cuentaTarjeta`, `cuentaTransferencias`); absent, «Bancos». A pending transfer's is
           * named when it is confirmed (`pago.confirmado`).
           */
          cuenta: cuentaDeTransferencia,
        }).superRefine((v, ctx) => {
          if (v.cuenta !== undefined && v.forma !== 'tarjeta' && !(v.forma === 'transferencia' && v.estado === 'confirmado')) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cuenta'], message: 'Solo una tarjeta o una transferencia confirmada va a una cuenta de banco' });
          }
        }),
      )
      .min(1),

    /** Change handed back, always in córdobas, and never more than the cash tendered. */
    cambio: z.number().int().nonnegative().default(0),
  }),

  /**
   * Void or full return.
   *
   * Unlike a purchase, this is safe at any time. A sale removes quantity at the running
   * average without changing it, so putting the goods back at the cost they left with is
   * arithmetically honest however many movements happened in between.
   */
  'venta.anulada': z.object({
    ventaId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
    /** False when the goods came back damaged: reverse the money, write the stock off. */
    reingresarStock: z.boolean().default(true),
    /**
     * Owner whose PIN let it through. Needed, unless the owner is the one acting, for anything
     * from before the shift still open, and for everything where the shop asks for it
     * (`anularConDueno`); refused if it is not an active owner (core `permisos.ts`).
     */
    autorizadoPor: z.string().uuid().optional(),
    /**
     * The open till the cash is handed back from, when the sale's own till has closed: its money
     * went to general cash at the corte. Needed then if the sale took any cash.
     */
    sesionId: z.string().uuid().optional(),
  }),

  /** A pending transfer or wallet payment, a sale's or a top-up's, seen in the bank or the wallet. */
  'pago.confirmado': z
    .object({
      ventaId: z.string().uuid().optional(),
      indice: z.number().int().nonnegative().optional(),
      recargaId: z.string().uuid().optional(),
      /** An abono by transfer or wallet taken by somebody who does not confirm them (`fiado.ts`). */
      abonoId: z.string().uuid().optional(),
      referencia: z.string().max(80).optional(),
      /**
       * The córdoba account a transfer was seen in (`cuentaTransferencias` unless whoever confirms
       * it says otherwise); absent, «Bancos». A wallet payment's goes to the wallet.
       */
      cuenta: cuentaDeTransferencia,
    })
    .superRefine(unPagoEnCola),

  /**
   * The money never arrived. The sale stands; the payment becomes a loss to chase — or, with
   * `aFiado`, what the sale's client owes, on their account like any fiado.
   */
  'pago.rechazado': z
    .object({
      ventaId: z.string().uuid().optional(),
      indice: z.number().int().nonnegative().optional(),
      recargaId: z.string().uuid().optional(),
      /** An abono's: what the client owes goes back up by it. */
      abonoId: z.string().uuid().optional(),
      motivo: z.string().min(1).max(200),
      aFiado: z.boolean().default(false),
    })
    .superRefine(unPagoEnCola),

  /**
   * Part of a sale coming back.
   *
   * A void undoes a whole sale; this undoes some of it: one bottle of three, a sack that turned
   * out to be torn. The refund is worked out by the reducer from what the lines were sold for,
   * the sale's discount included, never typed — and returning a sale in several goes adds up to
   * exactly what the whole of it was sold for.
   */
  'devolucion.registrada': z.object({
    devolucionId: z.string().uuid(),
    ventaId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The till a cash refund came out of. Required for one; ignored otherwise. */
    sesionId: z.string().uuid().optional(),
    lineas: z
      .array(
        z.object({
          /** Position of the line in the sale. */
          indice: z.number().int().nonnegative(),
          /** In sale units, milli. At most what was sold, less what already came back. */
          cantidad: z.number().int().positive(),
          /** False when it came back damaged: the money goes back, the goods go to merma. */
          reingresar: z.boolean().default(true),
        }),
      )
      .min(1),
    /**
     * How the customer gets the money back. `credito` takes it off what they owe, and needs the
     * sale to have been to a client.
     */
    reembolso: z.enum(['efectivo_cordobas', 'credito', 'transferencia', 'billetera']),
    /** The córdoba account a refund by transfer went out of. */
    cuenta: cuentaDeTransferencia,
    motivo: z.string().min(1).max(200),
    /**
     * Owner whose PIN let it through. Needed, unless the owner is the one acting, for anything
     * from before the shift still open, and for everything where the shop asks for it
     * (`anularConDueno`); refused if it is not an active owner (core `permisos.ts`).
     */
    autorizadoPor: z.string().uuid().optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.reembolso, ctx)),

  // ---------- Fiado ----------

  /**
   * A client paying something off what they owe.
   *
   * Cash taken at a till goes into that till, and names it, so the corte expects it. Anything
   * else — a transfer, a wallet, cash handed to the owner away from the counter — never touches
   * a drawer.
   */
  'abono.registrado': z.object({
    abonoId: z.string().uuid(),
    clienteId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().int().positive(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']),
    /** The till the cash went into. Cash needs one: it goes into an open till, never loose. */
    sesionId: z.string().uuid().optional(),
    /** The córdoba account a transfer went to. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
    nota: z.string().max(200).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /** A payment recorded by mistake. What the client owes goes back up by the same amount. */
  'abono.anulado': z.object({
    abonoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
    /**
     * Owner whose PIN let it through. Needed, unless the owner is the one acting, for anything
     * from before the shift still open, and for everything where the shop asks for it
     * (`anularConDueno`); refused if it is not an active owner (core `permisos.ts`).
     */
    autorizadoPor: z.string().uuid().optional(),
    /**
     * The open till the cash is handed back from, when the abono's own till has closed: its money
     * went to general cash at the corte. Needed then.
     */
    sesionId: z.string().uuid().optional(),
  }),

  /**
   * What a client owed that the shop will not collect: they moved away, they died, or the owner
   * let it go. Off what they owe and into an expense of its own, «Fiado incobrable», so the shop
   * can see what fiado costs it, instead of an abono that never happened. Never more than they
   * owe. The owner's (`dar_credito`).
   */
  'incobrable.registrado': z.object({
    incobrableId: z.string().uuid(),
    clienteId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().int().positive(),
    motivo: z.string().min(1).max(200),
  }),

  /** They came back and paid, or it was a mistake: what they owe goes back up by the same amount. */
  'incobrable.anulado': z.object({
    incobrableId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  // ---------- Recargas ----------

  /**
   * Buying phone credit in bulk, at a discount off face value.
   *
   * Carried as an asset at cost, averaged exactly like inventory. The margin is realised when
   * a recarga is sold at face — which is why a recarga is commission income and not a product
   * sale: booking C$100 of top-up as revenue would inflate sales by the full face value and
   * make every margin report meaningless.
   */
  'recarga.saldo_comprado': z
    .object({
      compraSaldoId: z.string().uuid(),
      operadora: z.string().min(1).max(30),
      /** Face value acquired. */
      montoFacial: z.number().int().positive(),
      /** What was paid for it: never more than it is worth, which would be money out with nothing for it. */
      costo: z.number().int().nonnegative(),
      formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']),
      /** The córdoba account a transfer came from. */
      cuenta: cuentaDeTransferencia,
    })
    .refine((p) => p.costo <= p.montoFacial, {
      message: 'El saldo no cuesta más que su valor facial',
      path: ['costo'],
    })
    .superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * The operator's real balance, read off its portal or its text message.
   *
   * Nothing else ever checked it: a top-up sold and never recorded — the cash pocketed — or one
   * given to a friend left the books' balance above the real one forever. The difference is
   * posted, valued at what the balance cost on average.
   */
  'recarga.saldo_contado': z.object({
    conteoId: z.string().uuid(),
    operadora: z.string().min(1).max(30),
    /** Face value the operator says is there now. */
    facial: z.number().int().nonnegative(),
    nota: z.string().max(300).optional(),
  }),

  'recarga.registrada': z.object({
    recargaId: z.string().uuid(),
    sesionId: z.string().uuid(),
    operadora: z.string().min(1).max(30),
    telefono: z.string().min(4).max(20),
    montoFacial: z.number().int().positive(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The receipt's reference, for a top-up paid by transfer or wallet: it waits in the queue. */
    referencia: z.string().max(80).optional(),
  }),

  // ---------- Planilla ----------

  /**
   * Alguien entra a la planilla.
   *
   * An employee is not a user: a bodeguero who never touches the system is still on the
   * payroll, and a user account that signs in is not necessarily paid a salary. Linking them is
   * optional and exists only so a payslip can carry the name the cashier is known by.
   */
  'empleado.contratado': z
    .object({
      empleadoId: z.string().uuid(),
      nombre: z.string().min(1).max(120),
      cedula: z.string().max(20).nullable().default(null),
      cargo: z.string().min(1).max(60),
      /**
       * Salario ordinario mensual, en centavos. Paid any other way (`tarifa`), what that comes to in
       * a month (`equivalenteMensual`): the measure of a salary for severance, and what a phone that
       * has not updated reads.
       */
      salarioMensual: z.number().int().positive(),
      fechaIngreso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /** Número de asegurado del INSS, cuando ya está inscrito. */
      inssNumero: z.string().max(30).nullable().default(null),
      telefono: z.string().max(40).nullable().default(null),
      usuarioId: z.string().uuid().nullable().default(null),
      formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('efectivo_cordobas'),
      /**
       * How often they are paid (the owner, 2026-10-12: «some people want to be paid weekly»): every
       * week, every fifteen days or every month. Absent, every month, as everybody was before.
       */
      frecuencia: z.enum(FRECUENCIAS_PAGO).default('mensual'),
      /** Paid so much for each day worked, the days typed at each run, rather than a fixed amount. */
      porDia: z.boolean().default(false),
      /** What is agreed, in centavos: for each day worked, or for each week or fifteen days. */
      tarifa: z.number().int().positive().optional(),
      /**
       * INSS — 7 % of their pay, and the shop's 21.5 % — and INATEC's 2 %: off, none of the three
       * (the owner, 2026-10-12). Absent, on, as for everybody before.
       */
      conInss: z.boolean().default(true),
      /** Aguinaldo and vacations accrue, and severance is paid when they leave; off, none of them. */
      conPrestaciones: z.boolean().default(true),
    })
    .superRefine(pagoCompleto),

  'empleado.actualizado': z.object({
    empleadoId: z.string().uuid(),
    nombre: z.string().min(1).max(120).optional(),
    cargo: z.string().min(1).max(60).optional(),
    /** A raise takes effect on the NEXT run; runs already closed are history. */
    salarioMensual: z.number().int().positive().optional(),
    cedula: z.string().max(20).nullable().optional(),
    inssNumero: z.string().max(30).nullable().optional(),
    telefono: z.string().max(40).nullable().optional(),
    usuarioId: z.string().uuid().nullable().optional(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).optional(),
    /**
     * Another way of paying them, from the next run on, said whole: the books refuse a change that
     * leaves it short (`pagoCompleto`). `tarifa` null when it becomes a fixed amount every month.
     */
    frecuencia: z.enum(FRECUENCIAS_PAGO).optional(),
    porDia: z.boolean().optional(),
    tarifa: z.number().int().positive().nullable().optional(),
    conInss: z.boolean().optional(),
    conPrestaciones: z.boolean().optional(),
    /**
     * The day they really started (2026-10-31): a hire takes the day it is entered unless changed,
     * and somebody on staff before the shop kept its payroll here could not be put in a run of
     * those days. Earlier is always taken; later, not past a day a run already paid them.
     */
    fechaIngreso: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),

  /**
   * Liquidación: el contrato termina.
   *
   * The severance itself is computed by the reducer from the hire date and the salary, not sent
   * by the device — the amounts are the law's, not an operator's opinion, and Art. 45 is
   * exactly the kind of arithmetic a person gets wrong under pressure.
   *
   * `despido_con_causa` pays no indemnización. Everything already accrued — aguinaldo and
   * vacaciones — is still owed and still paid, because those were earned.
   */
  'empleado.liquidado': z.object({
    empleadoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    motivo: z.enum(['despido_sin_causa', 'renuncia', 'mutuo_acuerdo', 'despido_con_causa']),
    /** Días del último mes ya trabajados y no incluidos en ninguna corrida. */
    salarioPendiente: z.number().int().nonnegative().default(0),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('efectivo_cordobas'),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    nota: z.string().max(300).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * Corrida de planilla: a month, half a month or a week of it.
   *
   * One event per run, carrying one line per employee. The device sends what varies — the days
   * somebody paid by the day worked, overtime, vacation days paid, aguinaldo in December — and the
   * reducer computes INSS, IR, employer cost and the provisions from the rates in force, so two
   * terminals cannot produce two different payrolls for the same days.
   *
   * Nothing is paid here. The run creates the obligation; handing the money over is a separate
   * event, because in practice they happen on different days and sometimes in different weeks.
   */
  'planilla.corrida': z
    .object({
      planillaId: z.string().uuid(),
      /**
       * `YYYY-MM`: the month the run belongs to, the month of `hasta` when it says one. Nobody is paid
       * twice for the same day: the books refuse a run with somebody another run already paid for a
       * day of it.
       */
      periodo: z.string().regex(/^\d{4}-\d{2}$/),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /**
       * Whose (2026-10-12): the people paid every week, every fifteen days or every month, and so what
       * a year of it is for the income tax. Absent, every month.
       */
      frecuencia: z.enum(FRECUENCIAS_PAGO).default('mensual'),
      /**
       * The days it pays, both or neither: a week, half a month or a month (`esRangoDe`). Absent, the
       * whole month of `periodo`, as every run was before.
       */
      desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
      hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
      lineas: z.array(lineaDePlanilla).min(1),
      nota: z.string().max(300).optional(),
    })
    .superRefine((v, ctx) => {
      if ((v.desde === undefined) !== (v.hasta === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hasta'], message: 'Una planilla dice desde qué día y hasta cuál, o ninguno' });
      }
      if (v.desde !== undefined && v.hasta !== undefined && v.desde > v.hasta) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hasta'], message: 'Una planilla termina después de empezar' });
      }
      if (v.hasta !== undefined && v.hasta.slice(0, 7) !== v.periodo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['periodo'], message: 'Una planilla es del mes en que termina' });
      }
    }),

  /**
   * Un adelanto de salario.
   *
   * Handed out as a drawer withdrawal, an advance was money gone with nothing saying who owed
   * it, and the next payroll paid the whole month again. Now the employee owes it, and the next
   * run (or a settlement) takes it off what they are handed.
   */
  'empleado.adelanto': z.object({
    adelantoId: z.string().uuid(),
    empleadoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().int().positive(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('efectivo_cordobas'),
    /** Paid out of the till open on this device: it leaves the drawer, and that corte expects it gone. */
    sesionId: z.string().uuid().optional(),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    nota: z.string().max(300).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * Días de vacaciones descansados, apuntados fuera de la planilla (the owner, 2026-10-31: «a button on
   * the payroll page that allows us to log used vacation days outside of payroll»).
   *
   * Nothing is handed over: the run of those days pays their salary as always, and what was set aside
   * for the vacation pays it, so much a day as it was set aside — the same the run does with the days
   * typed on its line. Past the days they have earned, it is paid leave, and nothing of what was set
   * aside goes to it.
   */
  'empleado.vacaciones': z
    .object({
      vacacionesId: z.string().uuid(),
      empleadoId: z.string().uuid(),
      desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /** The days rested, in thousandths: a day and a half, 1500. */
      dias: z.number().int().positive(),
      nota: z.string().max(300).optional(),
    })
    .refine((v) => v.desde <= v.hasta, { message: 'Las vacaciones terminan después de empezar', path: ['hasta'] }),

  /** Entregar el neto. Separado de la corrida porque casi nunca es el mismo día. */
  'planilla.pagada': z.object({
    /**
     * A run's id, or a settlement's: `liq:` and the person's (`empleado.liquidado`). A uuid alone
     * refused every settlement, and the screen said nothing (2026-10-13).
     */
    planillaId: z.string().regex(/^(liq:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('efectivo_cordobas'),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * A run set right (the owner, 2026-10-31: «we need a way to be able to edit payroll runs from the
   * runs tab for corrections»): its lines as they should have been, for the same days. The books take
   * back what it booked and book these instead, on its day; paid, the payment follows them, on its day
   * and out of what paid it (`pagoId`, its `planilla.pagada`). With no line, the run is voided.
   */
  'planilla.corregida': z.object({
    planillaId: z.string().uuid(),
    lineas: z.array(lineaDePlanilla),
    /** The run's payment, when it was paid: what hands over the difference, or takes it back. */
    pagoId: z.string().uuid().optional(),
    nota: z.string().max(300).optional(),
  }),

  /**
   * Pagar al INSS, al INATEC y a la DGI.
   *
   * Amounts come from the payload rather than from the liability balances: the institution
   * bills what it bills, and forcing the payment to equal the computed accrual would make the
   * books unable to represent the very discrepancy somebody needs to go and resolve.
   */
  'cotizaciones.pagadas': z.object({
    pagoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Período que se está cobrando, `YYYY-MM`. */
    periodo: z.string().regex(/^\d{4}-\d{2}$/),
    inss: z.number().int().nonnegative().default(0),
    inatec: z.number().int().nonnegative().default(0),
    ir: z.number().int().nonnegative().default(0),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('transferencia'),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * The aguinaldo paid on its own (the owner, 2026-10-31: «let's separate the pay aguinaldo function
   * as a button on the payroll page and take that shit out of the normal payroll form»): what each
   * person on it is handed, out of what the runs set aside for it and past that an expense, with no
   * INSS nor income tax (Art. 95 CT). Paid as it is written down, the day it was and the way.
   */
  'aguinaldo.pagado': z.object({
    pagoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    lineas: z.array(lineaDeAguinaldo).min(1),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']).default('efectivo_cordobas'),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * An aguinaldo payment set right (2026-10-31), as a run is (`planilla.corregida`): its lines as they
   * should have been, or none to void it. Its day and its way stay: what was handed over changes with
   * it, out of or back into what paid it.
   */
  'aguinaldo.corregido': z.object({
    pagoId: z.string().uuid(),
    lineas: z.array(lineaDeAguinaldo),
    nota: z.string().max(300).optional(),
  }),

  // ---------- Gastos y dinero del dueño ----------

  /**
   * Un gasto.
   *
   * Recorded when it is PAID, not when it is incurred. A pulpería does not accrue the light
   * bill; it pays it and the money is gone. The one place accrual already exists is a purchase
   * on credit, which creates a payable at the moment the goods arrive because the goods are
   * what create the debt.
   *
   * The category is a closed list rather than free text on purpose: a category somebody types
   * is a category nobody can report on, and "varios" written eleven different ways is how a
   * shop ends up unable to say what it spends money on.
   */
  'gasto.registrado': z
    .object({
      gastoId: z.string().uuid(),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      categoria: categoriaGastoSchema,
      descripcion: z.string().min(1).max(200),
      moneda: z.enum(['NIO', 'USD']).default('NIO'),
      /** In the currency named above. Rent in Nicaragua is very often quoted in dollars. */
      monto: z.number().int().positive(),
      /** C$ per USD × 10,000. Required when the expense is in dollars. */
      tasaCambio: z.number().int().positive().optional(),
      formaPago: formaPagoGastoSchema,
      /**
       * Paid out of an open till, in córdobas: it leaves the drawer instead of general cash, and that
       * till's corte expects it gone. Only with `efectivo_cordobas`. The till open on this device, or
       * since 2026-10-16 one open on another, which the owner pays from in «Gastos» (the owner: «We
       * need an option to be able to pay an expense from the till»): the books never asked whose.
       */
      sesionId: z.string().uuid().optional(),
      /**
       * The bank account a transfer came from: a dollar account only for an expense in dollars, which
       * it pays in its dollars, at the official rate as dollars from the safe do.
       */
      cuenta: cuentaBancoSchema.optional(),
      /**
       * Paid in parts, each its own way (the owner, 2026-10-16: «We need the option to pay with mixed
       * methods of payment»): some out of the drawer and the rest by transfer, say, or a part in cash
       * and the rest owed. `formaPago` pays what the parts leave, always something: on credit, what
       * is left is owed. Each part in the expense's currency, as `monto`. Absent, `formaPago` paid it
       * all, as every expense did before. As a purchase's parts are (`compra.registrada`).
       */
      pagos: z
        .array(
          z.object({
            pagoId: z.string().uuid(),
            formaPago: z.enum(['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera']),
            monto: z.number().int().positive(),
            /** Out of an open till, as the expense's own `sesionId`: córdobas in cash only. */
            sesionId: z.string().uuid().optional(),
            /** The córdoba account a part by transfer came from. */
            cuenta: cuentaDeTransferencia,
          }),
        )
        .min(1)
        .max(6)
        .optional(),
      /** Optional: who was paid, when it is somebody already in the supplier list. */
      proveedorId: z.string().uuid().nullable().default(null),
      /** Receipt or invoice number, when there is one. */
      referencia: z.string().max(80).optional(),
      nota: z.string().max(300).optional(),
      /** SHA-256 of a photo of the receipt: what the money went on, kept with it. */
      foto: fotoHashSchema.optional(),
      /**
       * A month of a fixed expense (`gastosFijos`), which «Por pagar» stops asking for. Its id is
       * then `idDeOcurrencia(id, periodo)`. A phone that has not updated ignores the field.
       */
      gastoFijo: z.object({ id: z.string().uuid(), periodo: periodoSchema }).optional(),
    })
    .superRefine((v, ctx) => {
      if (v.moneda === 'USD' && !v.tasaCambio) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tasaCambio'],
          message: 'Un gasto en dólares necesita la tasa de cambio usada',
        });
      }
      if (v.pagos) {
        if (v.pagos.reduce((a, x) => a + x.monto, 0) >= v.monto) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pagos'],
            message: 'Las partes pagan todo el gasto: la última va en la forma de pago',
          });
        }
        if (new Set(v.pagos.map((x) => x.pagoId)).size !== v.pagos.length) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'Dos partes con el mismo pago' });
        }
        if (v.pagos.some((x) => x.sesionId && x.formaPago !== 'efectivo_cordobas')) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'De la gaveta solo sale efectivo en córdobas' });
        }
        if (v.pagos.some((x) => x.cuenta !== undefined && x.formaPago !== 'transferencia')) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pagos'], message: 'Solo una transferencia va a una cuenta de banco' });
        }
      }
      cuentaSoloConTransferencia(v, v.formaPago, ctx);
      dolaresSoloEnDolares(v, ctx);
    }),

  /**
   * A shortage explained: what a count found missing was spent, on this. The owner says so, and
   * the books move it from the shortage to the expense, with no money moving: the count already
   * took it out of the cash. A till's shortage then stops being on whoever had the till, and the
   * profit shows what the money went on. Recorded as an expense the books can list and void
   * (`gasto.anulado`); its own kind, not a `gasto.registrado`, so a phone that has not updated
   * drops it rather than book the money as spent twice.
   */
  'faltante.explicado': z.object({
    gastoId: z.string().uuid(),
    /**
     * The count that found it missing: a till's corte, handover, opening or receipt, a route's
     * settlement, or the safe's count. Nothing else: the books refuse any other event. The expense
     * is on that count's day, where the shortage it takes the place of is.
     */
    eventoId: z.string().uuid(),
    categoria: z.enum([
      'alquiler',
      'energia',
      'agua',
      'telefono_internet',
      'transporte',
      'mantenimiento',
      'impuestos_municipales',
      'empaques',
      'sueldos',
      'otros',
    ]),
    descripcion: z.string().min(1).max(200),
    /** Córdobas, never more than what that count found missing and is not yet explained. */
    monto: z.number().int().positive(),
    nota: z.string().max(300).optional(),
  }),

  /** Reversing entry, not a deletion. A wrong expense is corrected by a new record. */
  'gasto.anulado': z.object({
    gastoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
    /**
     * A correction: the expense recorded, just before this, in its place. Refused unless that one
     * stands, so a correction whose new record was refused leaves the old one as it was instead of
     * voiding it with nothing in its place. Two events every version already reads, not a new
     * kind: a phone that has not updated drops an event it does not know, for good.
     */
    reemplazo: z.string().uuid().optional(),
    /**
     * The correction is recorded just after this, not before: the drawer could not pay the new
     * amount until the old one was back in it. `reemplazo` names it all the same, so the month of
     * a fixed expense it corrects is paid by it, and unpaid if it was refused; a void that named
     * nothing brought the month back as due, and one tap paid the rent twice (2026-10-13).
     */
    reemplazoDespues: z.literal(true).optional(),
    /**
     * Refused once the money it paid with was counted after it: out of a till, once that till was
     * handed over or closed; otherwise once general cash, the bank or the wallet was (`cuentasContadas`).
     * The count already found the money that never went and booked it, and the void would put it
     * back a second time. Sent by every void from this version on; one without it is from before,
     * and stands as it did then, so the log replays as it always has.
     */
    sinContar: z.literal(true).optional(),
  }),

  /**
   * Paying off a purchase taken on credit.
   *
   * Without this, `Cuentas por pagar` only ever grows and the balance sheet says the business
   * owes money it settled months ago.
   */
  'compra.pagada': z.object({
    compraId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().int().positive(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
    /**
     * Paid out of the till open on this device, in córdobas: it leaves the drawer instead of
     * general cash, and that till's corte expects it gone. Only with `efectivo_cordobas`.
     */
    sesionId: z.string().uuid().optional(),
    /**
     * Names the payment, so it can be voided (`compra.pago_anulado`). A payment from before
     * payments had one is kept as it is.
     */
    pagoId: z.string().uuid().optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * Paying an expense bought on credit (`gasto.registrado` with `credito`): the bill that came
   * before the money did. In parts or at once, never past what is owed.
   */
  'gasto.pagado': z.object({
    gastoId: z.string().uuid(),
    fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    monto: z.number().int().positive(),
    formaPago: z.enum(['efectivo_cordobas', 'transferencia', 'billetera']),
    /** The córdoba account a transfer came from. */
    cuenta: cuentaDeTransferencia,
    referencia: z.string().max(80).optional(),
    /**
     * Paid out of the till open on this device, in córdobas: it leaves the drawer instead of
     * general cash, and that till's corte expects it gone. Only with `efectivo_cordobas`.
     */
    sesionId: z.string().uuid().optional(),
    /** Names the payment, so it can be voided (`gasto.pago_anulado`). */
    pagoId: z.string().uuid().optional(),
  }).superRefine((v, ctx) => cuentaSoloConTransferencia(v, v.formaPago, ctx)),

  /**
   * A supplier's payment that was not, voided: what was paid goes back to where it came from and
   * is owed again. Not once the money it came from was counted after it (`cuentasContadas`), nor
   * from a till that has closed since: that count already found it.
   */
  'compra.pago_anulado': z.object({
    compraId: z.string().uuid(),
    pagoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),
  'gasto.pago_anulado': z.object({
    gastoId: z.string().uuid(),
    pagoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  /**
   * Money the owner takes out of the business.
   *
   * This is NOT an expense, and keeping the two apart is the single most load-bearing
   * distinction in a family business's books. C$2,000 taken for the week's groceries booked as
   * a cost makes every margin, every profit figure and every decision that rests on them wrong.
   * It reduces equity, which is exactly what it does in reality.
   */
  'dueno.retiro': z
    .object({
      movimientoId: z.string().uuid(),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /** In córdobas. Out of a dollar account, what its dollars come to at the official rate: the books work it out from `dolares`. */
      monto: z.number().int().positive(),
      origen: z.enum(['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera']),
      /** The bank account a transfer came out of (`bancos.ts`); absent, «Bancos». */
      cuenta: cuentaBancoSchema.optional(),
      /** USD cents, out of a dollar account: needed then, refused otherwise. */
      dolares: z.number().int().positive().optional(),
      motivo: z.string().max(200).optional(),
    })
    .superRefine((v, ctx) => dolaresDelDueno(v, v.origen, ctx)),

  /** Money the owner puts in. The mirror image: equity up, cash up, no income. */
  'dueno.aporte': z
    .object({
      movimientoId: z.string().uuid(),
      fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      /** In córdobas. Into a dollar account, what its dollars come to at the official rate: the books work it out from `dolares`. */
      monto: z.number().int().positive(),
      destino: z.enum(['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera']),
      /** The bank account a transfer went to (`bancos.ts`); absent, «Bancos». */
      cuenta: cuentaBancoSchema.optional(),
      /** USD cents, into a dollar account: needed then, refused otherwise. */
      dolares: z.number().int().positive().optional(),
      motivo: z.string().max(200).optional(),
    })
    .superRefine((v, ctx) => dolaresDelDueno(v, v.destino, ctx)),

  /**
   * Money for or from the owner that was not, voided: it goes back to where it came from. Not
   * once that money was counted after it (`cuentasContadas`): the count already found it.
   */
  'dueno.movimiento_anulado': z.object({
    movimientoId: z.string().uuid(),
    motivo: z.string().min(1).max(200),
  }),

  // ---------- Configuración ----------
  'config.cambiada': z.object({
    clave: z.string().min(1),
    valor: z.unknown(),
    /** Previous value, carried so the bitácora shows the change rather than just the result. */
    anterior: z.unknown().optional(),
  }),
} as const satisfies Record<string, z.ZodTypeAny>;

export type EventType = keyof typeof EVENT_SCHEMAS;
export type PayloadOf<T extends EventType> = z.infer<(typeof EVENT_SCHEMAS)[T]>;

/**
 * What a caller has to hand in, as opposed to what comes back out.
 *
 * Fields with a `.default()` are optional here and filled in by parsing. Emitting against the
 * output type instead would force every screen to spell out every default, which defeats the
 * point of having defaults and means adding one is a breaking change across the whole app.
 */
export type PayloadInputOf<T extends EventType> = z.input<(typeof EVENT_SCHEMAS)[T]>;

export const EVENT_TYPES = Object.keys(EVENT_SCHEMAS) as EventType[];

export function isKnownEventType(t: string): t is EventType {
  return Object.prototype.hasOwnProperty.call(EVENT_SCHEMAS, t);
}
